/** Registration desk data access: lookup by CI, walk-in registration, charge + receipt */

import { supabase } from '@/supabase.config';
import { registerParticipant, type MinisterPricingOverride } from '@/Services/eventParticipants.service';
import { mapAuthorityPositionToRoleCode } from '@/Services/pricingEngine.service';
import { linkPaymentReceipt, recordPayment, type EventPaymentMethod } from '@/Services/eventPayments.service';
import { getActiveReceiptCategories, issueReceipt } from '@/features/receipts/data/receiptsRepository';
import { amountToWords } from '@/features/receipts/utils/numberToWords';
import { parseLocalDate } from '@/models/event';
import type { DeskPricing, DeskRow } from './deskLogic';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export async function lookupDesk(eventId: string, ci: string): Promise<DeskRow[]> {
  const { data, error } = await db.rpc('desk_lookup', { p_event_id: eventId, p_ci: ci });
  if (error) throw new Error(error.message);
  return (data ?? []) as DeskRow[];
}

/** Minister attributes the pricing rules look at, from the lookup row */
export function toPricingOverride(pricing: DeskPricing): MinisterPricingOverride {
  return {
    minister_degree: pricing.degree_id || undefined,
    minister_district: pricing.district_id || undefined,
    minister_region: pricing.region_id || undefined,
    minister_birth_date: pricing.birth_date ? parseLocalDate(pricing.birth_date) : undefined,
    minister_role: pricing.position || undefined,
    minister_roles: pricing.authority_roles
      .map((r) => mapAuthorityPositionToRoleCode(r.org_type, r.role))
      .filter((c): c is string => !!c),
  };
}

/** What the desk collects when registering a minister that did not pre-register online */
export interface DeskMinisterRegistration {
  /** Form data shown on the printable form (contact data, birth date…) */
  registration_data: Record<string, unknown>;
  /** Birth date as edited at the desk: age-based discounts use it */
  birth_date: string | null;
  documents: Record<string, string>;
  declaration_accepted: boolean;
  signed: boolean;
}

/** On-site registration of a minister: verified right away, priced with the event's rules */
export async function registerMinisterAtDesk(
  eventId: string,
  ministerId: string,
  pricing: DeskPricing,
  extras?: DeskMinisterRegistration,
) {
  return registerParticipant({
    event_id: eventId,
    minister_id: ministerId,
    calculate_price: true,
    minister_pricing_data: toPricingOverride(extras ? { ...pricing, birth_date: extras.birth_date } : pricing),
    ...(extras
      ? {
          registration_data: extras.registration_data,
          documents: extras.documents,
          declaration_accepted: extras.declaration_accepted,
          form_signed: extras.signed,
        }
      : {}),
  });
}

/** Handing the credential over marks the person as present at the event (general attendance) */
export async function markAttended(participantId: string): Promise<{ attendance_status: string; checked_in_at: string }> {
  const { data, error } = await db.rpc('desk_mark_attended', { p_participant_id: participantId });
  if (error) throw new Error(error.message);
  return data;
}

export async function reviewRegistration(participantId: string, action: 'VERIFY' | 'REJECT', reason?: string) {
  const { error } = await db.rpc('review_preregistration', {
    p_participant_id: participantId,
    p_action: action,
    p_reason: action === 'REJECT' ? (reason ?? null) : null,
  });
  if (error) throw new Error(error.message);
}

export interface DeskChargeInput {
  eventName: string;
  participantId: string;
  payerName: string;
  payerCi: string | null;
  ministerId: string | null;
  amount: number;
  method: EventPaymentMethod;
  /** Bank voucher photo (transfer / QR) kept with the payment for the record */
  voucherKey?: string | null;
}

/** Issues the official receipt (Ingresos / otras ofrendas) for a recorded payment and links it */
export async function issueReceiptForPayment(
  input: Omit<DeskChargeInput, 'participantId'> & { paymentId: string },
): Promise<{ id: string; number: number; year: number }> {
  const categories = await getActiveReceiptCategories();
  const category = categories.find((c) => c.code === 'ING');
  if (!category) throw new Error('No existe la categoría de recibo «Ingresos / otras ofrendas»');

  const amount = input.amount.toFixed(2);
  const receipt = await issueReceipt(
    {
      category_id: category.id,
      payer_name: input.payerName,
      payer_ci: input.payerCi ?? '',
      currency: 'BOB',
      exchange_rate: '',
      discount: '',
      source_type: 'event_payment',
      source_id: input.paymentId,
      minister_id: input.ministerId,
      lines: [{ concept_id: '', description: `Inscripción — ${input.eventName}`, quantity: '1', unit_price: amount }],
      payments: [{ method: input.method, amount, bank_account_id: '', check_number: '', voucher_photo_url: '' }],
    },
    amountToWords(input.amount),
  );
  await linkPaymentReceipt(input.paymentId, receipt.id);
  return receipt;
}

/**
 * Records the payment and issues its receipt in one step. If the receipt fails the payment stays
 * recorded; the card then offers "Emitir recibo" so nothing is charged twice.
 */
export async function chargeAndIssueReceipt(
  input: DeskChargeInput,
): Promise<{ paymentId: string; receipt: { id: string; number: number; year: number } | null; receiptError?: string }> {
  const paymentId = await recordPayment({
    participant_id: input.participantId,
    payment_amount: input.amount,
    method: input.method,
    voucher_key: input.voucherKey ?? null,
    notes: 'Cobro en mesa de registro',
  });
  try {
    const receipt = await issueReceiptForPayment({ ...input, paymentId });
    return { paymentId, receipt };
  } catch (err) {
    return { paymentId, receipt: null, receiptError: err instanceof Error ? err.message : 'No se pudo emitir el recibo' };
  }
}
